import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Copy,
  Clock,
  CheckCheck,
  CircleCheck,
  Undo2,
  MoreVertical,
} from "lucide-react";
import {
  ethereum,
  solana,
  bitcoin,
  base,
  optimism,
  lightning,
  tron,
} from "@paywithglide/glide-js/chains";
import {
  Session,
  waitForSession,
  InsufficientPaymentAmountError,
  Currency,
  Chain,
} from "@paywithglide/glide-js";
import ModalBackButton from "../components/ModalBackButton";
import PrimaryButton from "../components/PrimaryButton";
import Alert from "../components/Alert";
import Warning from "../components/Warning";
import SecondaryButton from "../components/SecondaryButton";
import WarningAlert from "../components/WarningAlert";
import QRCode from "../components/QRCode";
import { LogoWithBadge } from "../components/LogoWithBadge";
import { useGlideDepositConfig } from "../lib/useDepositConfig";
import { logger } from "../lib/logger";
import { CombinedTokenChainSelector } from "../components/CombinedTokenChainSelector";
import { saveTokenSelection } from "../lib/tokenSelectionStorage";
import { getAllowedMultichainChains } from "../lib/multichainEligibility";
import {
  formatDisplayAmount,
  formatRemainingAmount,
} from "../lib/formatDisplayAmount";
import { subtractDecimalStrings } from "../lib/subtractDecimalStrings";
import { requiredPaymentAmount } from "../lib/requiredPaymentAmount";
import { getETA } from "../lib/getETAWarning";
import { motion, AnimatePresence } from "framer-motion";
import { animations } from "../lib/animations";

import {
  isOverpaymentAboveThreshold,
  MIN_OVERPAYMENT_THRESHOLD_USD,
} from "../lib/overpayment";

// Feature flag to enable/disable Lightning Network functionality
// Set to true to re-enable Lightning toggle and functionality
const ENABLE_LIGHTNING_FEATURE = false;

type ChainOption = "evm" | "solana" | "bitcoin" | "tron";

type Props = {
  onBack?: () => void;
  onSuccess: (session: Session) => void;
  selectedChainType: "evm" | "solana" | "bitcoin" | "tron";
  selectedCurrency?: Currency;
  selectedCurrencies?: Currency[]; // All currency objects with the same symbol
  allAvailableCurrencies?: Currency[]; // All currencies available for selection
  // Session props for reusing existing sessions
  evmSession?: Session;
  solanaSession?: Session;
  bitcoinSession?: Session;
  bitcoinLightningSession?: Session;
  tronSession?: Session;
  // Callbacks to pass sessions back to parent
  onSessionCreated?: (
    chainType: "evm" | "solana" | "bitcoin" | "tron",
    session: Session,
  ) => void;
  onLightningSessionCreated?: (session: Session) => void;
  onInsufficientPaymentAmount?: (session: Session) => void;
  onRequestRefund?: (session: Session) => void;
  onOverpaymentRefund?: (session: Session) => void;
  excludeBitcoin?: boolean;
  // Function to create a new session
  onCreateSession?: (
    chainType: "evm" | "solana" | "bitcoin" | "tron",
    specificChain?: Chain,
    currency?: Currency,
  ) => Promise<void>;
  /**
   * Regenerates the deposit address for the current chain without the stable
   * deposit address key, so the backend mints a fresh address. When omitted,
   * the overflow menu is hidden entirely.
   */
  onRegenerateAddress?: (
    chainType: "evm" | "solana" | "bitcoin" | "tron",
    specificChain?: Chain,
    currency?: Currency,
  ) => Promise<void>;
};

export default function MultichainTransfer({
  onBack,
  onSuccess,
  selectedChainType,
  selectedCurrency,
  selectedCurrencies,
  allAvailableCurrencies,
  evmSession: externalEvmSession,
  solanaSession: externalSolanaSession,
  bitcoinSession: externalBitcoinSession,
  bitcoinLightningSession: externalBitcoinLightningSession,
  tronSession: externalTronSession,
  onSessionCreated,
  onInsufficientPaymentAmount,
  onRequestRefund,
  onOverpaymentRefund,
  excludeBitcoin,
  onCreateSession,
  onRegenerateAddress,
}: Props) {
  const { glideConfig, ...depositConfig } = useGlideDepositConfig();

  const currencyVariants = useMemo(() => {
    if (selectedCurrencies && selectedCurrencies.length > 0) {
      return selectedCurrencies;
    }
    return selectedCurrency ? [selectedCurrency] : [];
  }, [selectedCurrencies, selectedCurrency]);

  // Abort controller for cancelling polling on back navigation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Handle back navigation with polling cancellation
  const handleBack = useCallback(() => {
    // Cancel ongoing polling when user navigates back
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    onBack?.();
  }, [onBack]);

  // Handle resuming polling when user indicates they've completed the transfer
  const handleResumePolling = useCallback(() => {
    setStep("waiting_for_transfer");
  }, []);

  const [step, setStep] = useState<"waiting_for_transfer" | "wait_for_session">(
    "waiting_for_transfer",
  );
  // Update indicator kept for polling feedback logic; not used in UI
  const [showUpdateIndicator, setShowUpdateIndicator] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(!document.hidden);
  const [paymentStatus, setPaymentStatus] = useState<string>("unpaid");
  const didTriggerOverpaymentRef = useRef(false);

  // UX state for the new flow
  const [didUserConfirmDeposit, setDidUserConfirmDeposit] = useState(false);
  const [didCopyAddress, setDidCopyAddress] = useState(false);
  const [isAddressMenuOpen, setIsAddressMenuOpen] = useState(false);
  const [isRegeneratingAddress, setIsRegeneratingAddress] = useState(false);
  const [didCopyAmount, setDidCopyAmount] = useState(false);
  const [didCopyRemaining, setDidCopyRemaining] = useState(false);
  const [useLightningOnly, setUseLightningOnly] = useState(false);
  const [incompletePollingError, setIncompletePollingError] = useState(false);
  // Loading state for dropdown selection
  const [isCreatingChainSession, setIsCreatingChainSession] = useState(false);
  // Local processing screen after user taps Deposit complete

  const allowedChains = useMemo(
    () =>
      getAllowedMultichainChains(glideConfig.chains, {
        destinationIsTestnet: depositConfig.chain?.isTestnet,
        chainIds: depositConfig.chainIds,
        excludeChainIds: depositConfig.excludeChainIds,
        excludeBitcoin,
      }),
    [
      glideConfig.chains,
      depositConfig.chain?.isTestnet,
      depositConfig.chainIds,
      depositConfig.excludeChainIds,
      excludeBitcoin,
    ],
  );

  // Filter currencies to only include those supported on at least one allowed chain,
  // then sort by allowedPaymentCurrencies order if specified.
  const filteredCurrencies = useMemo(() => {
    if (!allAvailableCurrencies || allAvailableCurrencies.length === 0) {
      return [];
    }

    const filtered = allAvailableCurrencies.filter((curr) => {
      for (const chain of allowedChains) {
        try {
          curr.on(chain);
          return true;
        } catch {
          // not supported on this chain, try next
        }
      }
      return false;
    });

    if (depositConfig.allowedPaymentCurrencies?.length) {
      const allowedSymbolOrder = new Map<string, number>();
      depositConfig.allowedPaymentCurrencies.forEach((allowedId, index) => {
        const match = allAvailableCurrencies.find(
          (c) => c.id.toLowerCase() === allowedId.toLowerCase(),
        );
        if (match && !allowedSymbolOrder.has(match.symbol)) {
          allowedSymbolOrder.set(match.symbol, index);
        }
      });
      filtered.sort((a, b) => {
        const aIndex =
          allowedSymbolOrder.get(a.symbol) ?? Number.MAX_SAFE_INTEGER;
        const bIndex =
          allowedSymbolOrder.get(b.symbol) ?? Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
    }

    return filtered;
  }, [
    allAvailableCurrencies,
    allowedChains,
    depositConfig.allowedPaymentCurrencies,
  ]);

  // Deduplicated token count — used to decide whether to show the selector.
  // CombinedTokenChainSelector renders from filteredCurrencies directly.
  const tokenOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const curr of filteredCurrencies) {
      seen.add(curr.symbol);
    }
    // Fall back to selectedCurrencies symbols if filteredCurrencies is empty
    if (seen.size === 0 && selectedCurrencies) {
      for (const curr of selectedCurrencies) seen.add(curr.symbol);
    }
    if (seen.size === 0 && selectedCurrency) seen.add(selectedCurrency.symbol);
    return { length: seen.size };
  }, [filteredCurrencies, selectedCurrencies, selectedCurrency]);

  // Build chain dropdown options - show INDIVIDUAL chains that support the selected currency
  const chainOptions = useMemo(() => {
    if (currencyVariants.length === 0) return [];

    const chains: Array<{
      id: string;
      label: string;
      logoUrl: string;
      chainObject: Chain;
    }> = [];

    // Check all chains in glideConfig
    for (const chain of allowedChains) {
      const supportedVariant = currencyVariants.find((currency) => {
        try {
          currency.on(chain);
          return true;
        } catch {
          return false;
        }
      });

      if (supportedVariant) {
        chains.push({
          id: chain.id,
          label: chain.name,
          logoUrl: chain.logoUrl || "",
          chainObject: chain,
        });
      }
    }

    return chains;
  }, [currencyVariants, allowedChains]);

  // Handle combined token + chain selection from the new combined selector
  const handleCombinedSelection = useCallback(
    async (currencyId: string, chainId: string) => {
      // Don't allow changes while already creating a session
      if (isCreatingChainSession) return;
      if (!onCreateSession) return;

      setIsCreatingChainSession(true);

      try {
        // Find the selected chain
        const selectedChain = glideConfig.chains.find((c) => c.id === chainId);
        if (!selectedChain) {
          logger.error(`Chain not found: ${chainId}`);
          return;
        }

        // Find the selected currency
        const selectedCurrencyObj =
          allAvailableCurrencies?.find((c) => c.id === currencyId) ||
          selectedCurrencies?.find((c) => c.id === currencyId);

        if (!selectedCurrencyObj) {
          logger.error(`Currency not found: ${currencyId}`);
          return;
        }

        // Determine chain type
        let chainType: ChainOption = "evm";
        const caip19 = selectedCurrencyObj.on(selectedChain);
        if (caip19.startsWith("eip155:")) {
          chainType = "evm";
        } else if (caip19.startsWith("solana:")) {
          chainType = "solana";
        } else if (caip19.startsWith("bip122:")) {
          chainType = "bitcoin";
        } else if (caip19.startsWith("tron:")) {
          chainType = "tron";
        }

        // Save to localStorage
        saveTokenSelection(selectedCurrencyObj, chainType, selectedChain.id);

        // Create new session with the selected currency and chain
        await onCreateSession(chainType, selectedChain, selectedCurrencyObj);
      } catch (error) {
        logger.error(
          `Failed to create session for combined selection: ${error}`,
        );
      } finally {
        setIsCreatingChainSession(false);
      }
    },
    [
      glideConfig.chains,
      allAvailableCurrencies,
      selectedCurrencies,
      onCreateSession,
      isCreatingChainSession,
    ],
  );

  // Determine if selected currency is available on only one chain
  const isSingleChainCurrency = useMemo(() => {
    if (currencyVariants.length === 0) return false;

    let totalChainCount = 0;

    // Check Ethereum chains - count how many individual Ethereum chains support this currency (excluding testnets)
    const evmChains = allowedChains.filter((chain) => {
      return currencyVariants.some((currency) => {
        try {
          const caip19 = currency.on(chain);
          return caip19.startsWith("eip155:") && !chain.isTestnet;
        } catch {
          return false;
        }
      });
    });
    totalChainCount += evmChains.length;

    // Check Solana
    if (
      allowedChains.some((chain) =>
        currencyVariants.some((currency) => {
          try {
            const caip19 = currency.on(chain);
            return caip19.startsWith("solana:");
          } catch {
            return false;
          }
        }),
      )
    ) {
      totalChainCount += 1;
    }

    // Check Bitcoin
    if (
      allowedChains.some((chain) =>
        currencyVariants.some((currency) => {
          try {
            const caip19 = currency.on(chain);
            return caip19.startsWith("bip122:");
          } catch {
            return false;
          }
        }),
      )
    ) {
      totalChainCount += 1;
    }

    // Check Tron
    if (
      allowedChains.some((chain) =>
        currencyVariants.some((currency) => {
          try {
            const caip19 = currency.on(chain);
            return caip19.startsWith("tron:");
          } catch {
            return false;
          }
        }),
      )
    ) {
      totalChainCount += 1;
    }

    // Single chain only if total count is exactly 1
    return totalChainCount === 1;
  }, [currencyVariants, allowedChains]);

  // For Bitcoin, wait for Lightning session if it's allowed and not excluded
  const shouldWaitForLightning =
    ENABLE_LIGHTNING_FEATURE &&
    selectedChainType === "bitcoin" &&
    !depositConfig.excludeChainIds?.includes(lightning.id) &&
    (!depositConfig.chainIds?.length ||
      depositConfig.chainIds.includes(lightning.id));

  // Session states - use external sessions (now always provided from parent)
  const evmSession = externalEvmSession;
  const solanaSession = externalSolanaSession;
  const bitcoinSession = externalBitcoinSession;
  const bitcoinLightningSession = externalBitcoinLightningSession;
  const tronSession = externalTronSession;

  const chainInfo = useMemo(() => {
    switch (selectedChainType) {
      case "evm": {
        // Check if selected currency is only available on one specific Ethereum chain
        let evmChainName = "Ethereum";
        let evmLogoUrls = [ethereum.logoUrl, base.logoUrl, optimism.logoUrl];

        if (selectedCurrency) {
          // Use all currencies with the same symbol if available, otherwise fall back to single currency
          const currenciesToCheck = selectedCurrencies || [selectedCurrency];

          const evmChains = allowedChains.filter((chain) => {
            // Check if any currency in the group supports this chain
            for (const curr of currenciesToCheck) {
              try {
                const caip19 = curr.on(chain);
                // Filter out testnet chains
                if (caip19.startsWith("eip155:") && !chain.isTestnet) {
                  return true;
                }
              } catch {
                // Currency not supported on this chain
              }
            }
            return false;
          });
          if (evmChains.length === 1) {
            evmChainName = evmChains[0].name;
            evmLogoUrls = [evmChains[0].logoUrl];
          } else if (evmSession?.paymentChainName) {
            evmChainName = evmSession.paymentChainName;
          }
        }

        return {
          name: evmChainName,
          address: evmSession?.depositAddress || "Generating deposit address",
          logoUrls: evmLogoUrls,
          session: evmSession,
          isPlaceholder: !evmSession?.depositAddress,
        };
      }
      case "solana":
        return {
          name: "Solana",
          address:
            solanaSession?.depositAddress || "Generating deposit address",
          logoUrls: [solana.logoUrl],
          session: solanaSession,
          isPlaceholder: !solanaSession?.depositAddress,
        };
      case "bitcoin": {
        // Use Lightning address if Lightning-only mode is enabled
        const btcAddress =
          useLightningOnly && shouldWaitForLightning
            ? bitcoinLightningSession?.depositAddress ||
              "Generating lightning invoice"
            : bitcoinSession?.depositAddress || "Generating deposit address";
        const btcIsPlaceholder =
          useLightningOnly && shouldWaitForLightning
            ? !bitcoinLightningSession?.depositAddress
            : !bitcoinSession?.depositAddress;
        return {
          name: "Bitcoin",
          address: btcAddress,
          logoUrls: [bitcoin.logoUrl],
          session: useLightningOnly ? bitcoinLightningSession : bitcoinSession,
          isPlaceholder: btcIsPlaceholder,
        };
      }
      case "tron":
        return {
          name: "Tron",
          address: tronSession?.depositAddress || "Generating deposit address",
          logoUrls: [tron.logoUrl],
          session: tronSession,
          isPlaceholder: !tronSession?.depositAddress,
        };
      default:
        return {
          name: "Ethereum",
          address: evmSession?.depositAddress || "Generating deposit address",
          logoUrls: [ethereum.logoUrl, base.logoUrl, optimism.logoUrl],
          session: evmSession,
          isPlaceholder: !evmSession?.depositAddress,
        };
    }
  }, [
    selectedChainType,
    evmSession,
    solanaSession,
    bitcoinSession,
    bitcoinLightningSession,
    tronSession,
    useLightningOnly,
    shouldWaitForLightning,
    selectedCurrency,
    selectedCurrencies,
    allowedChains,
  ]);

  const activeSession = chainInfo.session;

  const copyAddress = async () => {
    if (chainInfo.isPlaceholder) return;
    await navigator.clipboard.writeText(chainInfo.address);
    setDidCopyAddress(true);
    setTimeout(() => setDidCopyAddress(false), 2000);
  };

  const handleRegenerateAddress = async () => {
    if (!onRegenerateAddress || isRegeneratingAddress) return;
    setIsAddressMenuOpen(false);
    setIsRegeneratingAddress(true);
    try {
      // Regenerate the SAME chain (not a re-derived one): resolve the current
      // chain from the active session so only the address changes.
      const currentChain = activeSession?.paymentChainId
        ? glideConfig.chains.find((c) => c.id === activeSession.paymentChainId)
        : undefined;
      await onRegenerateAddress(
        selectedChainType,
        currentChain,
        selectedCurrency,
      );
    } finally {
      setIsRegeneratingAddress(false);
    }
  };

  const isIncompletePayment =
    depositConfig.mode === "pay" &&
    !!activeSession?.actualPaymentAmount &&
    Number(activeSession.actualPaymentAmount) > 0 &&
    !!requiredPaymentAmount(activeSession) &&
    Number(activeSession.actualPaymentAmount) <
      Number(requiredPaymentAmount(activeSession));

  const tokenPriceUSD = useMemo(() => {
    if (chainInfo.session) {
      const paymentAmount = Number(chainInfo.session.paymentAmount);
      const paymentAmountUSD = Number(chainInfo.session.paymentAmountUSD);
      if (paymentAmount > 0 && paymentAmountUSD > 0) {
        return paymentAmountUSD / paymentAmount;
      }
    }
    return undefined;
  }, [chainInfo.session]);

  const formattedPaymentAmount = useMemo(() => {
    if (chainInfo.session) {
      return formatDisplayAmount(
        chainInfo.session.paymentAmount,
        tokenPriceUSD,
      );
    }
    return "";
  }, [chainInfo.session, tokenPriceUSD]);

  // Session creation moved to parent (DepositWithGlideModal) for preloading

  // Get QR value for different chain types
  const qrCodeValue = useMemo(() => {
    if (
      chainInfo.isPlaceholder ||
      !chainInfo.address ||
      chainInfo.address === "Generating deposit address" ||
      chainInfo.address === "Generating lightning invoice"
    ) {
      return "";
    }

    // Bitcoin: toggle between plain address and Lightning invoice
    if (selectedChainType === "bitcoin") {
      if (useLightningOnly && shouldWaitForLightning) {
        // Lightning toggle ON: show Lightning invoice
        return chainInfo.address; // Lightning invoice
      } else {
        // Lightning toggle OFF (default): show plain Bitcoin address
        return chainInfo.address; // Plain BTC address
      }
    }

    return chainInfo.address;
  }, [
    chainInfo.address,
    chainInfo.isPlaceholder,
    selectedChainType,
    useLightningOnly,
    shouldWaitForLightning,
  ]);

  // Handle document visibility changes to pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsDocumentVisible(isVisible);

      if (!isVisible) {
        // Document became hidden, abort polling
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
      }

      // Note: polling will automatically resume when document becomes visible
      // due to the useEffect dependency on isDocumentVisible
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const isPollingAborted = abortControllerRef.current?.signal.aborted ?? false;

  const isBitcoinSession = chainInfo.session?.paymentChainId === bitcoin.id;

  // Session creation is now handled in parent (DepositWithGlideModal) before navigation
  // This ensures QR code is ready immediately when this component renders

  // Monitor transfers for current session with abort signal for back navigation
  useEffect(() => {
    // Only monitor if we're waiting for transfer or processing the session and polling is not aborted
    if (step !== "waiting_for_transfer" && step !== "wait_for_session") return;
    // Don't poll when document is hidden
    if (!isDocumentVisible) return;

    const currentSession = chainInfo.session;
    if (!currentSession) return;

    // Create new abort controller for this polling session
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const startPolling = async () => {
      try {
        const waitPromises = [
          waitForSession(glideConfig, {
            sessionId: currentSession.sessionId,
            signal: abortController.signal,
            onPollError: (error) => {
              logger.error(`[MultichainTransfer/PollError] ${error}`);
            },
            onUpdate: (updatedSession) => {
              setShowUpdateIndicator(true);
              setTimeout(() => setShowUpdateIndicator(false), 1000);
              setPaymentStatus(updatedSession.paymentStatus);

              if (updatedSession.paymentStatus === "paid") {
                onSuccess(updatedSession);
              }

              // Detect partial payment — stay on this screen with inline alert
              if (
                depositConfig.mode === "pay" &&
                updatedSession.paymentStatus === "unpaid" &&
                updatedSession.actualPaymentAmount &&
                Number(updatedSession.actualPaymentAmount) > 0 &&
                updatedSession.paymentAmount
              ) {
                const paid = Number(updatedSession.actualPaymentAmount);
                const required = Number(requiredPaymentAmount(updatedSession));
                if (paid < required) {
                  abortController.abort();
                  onSessionCreated?.(selectedChainType, updatedSession);
                  return;
                }
              }

              if (
                isBitcoinSession &&
                updatedSession.paymentStatus === "waiting_confirmations"
              ) {
                onSuccess(updatedSession);
              }
              if (
                depositConfig.mode === "pay" &&
                updatedSession.paymentStatus === "pending_refund" &&
                onInsufficientPaymentAmount
              ) {
                abortController.abort();
                onInsufficientPaymentAmount(updatedSession);
              }

              // Detect overpayment - only in pay mode, only after funds arrive
              const isFunded =
                updatedSession.paymentStatus === "waiting_confirmations" ||
                updatedSession.paymentStatus === "paid";

              if (
                depositConfig.mode === "pay" &&
                isFunded &&
                onOverpaymentRefund &&
                updatedSession.paymentAmountUSD &&
                updatedSession.actualPaymentAmountUSD &&
                !didTriggerOverpaymentRef.current
              ) {
                const requiredAmount = Number(updatedSession.paymentAmountUSD);
                const paidAmount = Number(
                  updatedSession.actualPaymentAmountUSD,
                );
                const deltaAmount = paidAmount - requiredAmount;
                const shouldTrigger = isOverpaymentAboveThreshold(
                  requiredAmount,
                  paidAmount,
                );
                logger.info(
                  `[OverpaymentCheck][MultichainTransfer] sessionId=${updatedSession.sessionId} status=${updatedSession.paymentStatus} requiredUSD=${requiredAmount} paidUSD=${paidAmount} deltaUSD=${deltaAmount} thresholdUSD=${MIN_OVERPAYMENT_THRESHOLD_USD} trigger=${shouldTrigger}`,
                );

                if (shouldTrigger) {
                  didTriggerOverpaymentRef.current = true;
                  onOverpaymentRefund(updatedSession);
                }
              }

              window.parent?.postMessage(
                { type: "sessionUpdate", session: updatedSession },
                "*",
              );
            },
          }),
        ];

        // Add Lightning session monitoring ONLY for Bitcoin
        if (selectedChainType === "bitcoin" && bitcoinLightningSession) {
          waitPromises.push(
            waitForSession(glideConfig, {
              sessionId: bitcoinLightningSession.sessionId,
              signal: abortController.signal,
              onUpdate: (updatedSession) => {
                setShowUpdateIndicator(true);
                setTimeout(() => setShowUpdateIndicator(false), 1000);
                setPaymentStatus(updatedSession.paymentStatus);

                if (updatedSession.paymentStatus === "paid") {
                  onSuccess(updatedSession);
                }
                if (updatedSession.paymentStatus === "waiting_confirmations") {
                  onSuccess(updatedSession);
                }
                if (
                  depositConfig.mode === "pay" &&
                  updatedSession.paymentStatus === "pending_refund" &&
                  onInsufficientPaymentAmount
                ) {
                  abortController.abort();
                  onInsufficientPaymentAmount(updatedSession);
                }

                // Detect overpayment - only in pay mode, only after funds arrive
                const isFunded =
                  updatedSession.paymentStatus === "waiting_confirmations" ||
                  updatedSession.paymentStatus === "paid";

                if (
                  depositConfig.mode === "pay" &&
                  isFunded &&
                  onOverpaymentRefund &&
                  updatedSession.paymentAmountUSD &&
                  updatedSession.actualPaymentAmountUSD &&
                  !didTriggerOverpaymentRef.current
                ) {
                  const requiredAmount = Number(
                    updatedSession.paymentAmountUSD,
                  );
                  const paidAmount = Number(
                    updatedSession.actualPaymentAmountUSD,
                  );
                  const deltaAmount = paidAmount - requiredAmount;
                  const shouldTrigger = isOverpaymentAboveThreshold(
                    requiredAmount,
                    paidAmount,
                  );
                  logger.info(
                    `[OverpaymentCheck][MultichainTransfer/Lightning] sessionId=${updatedSession.sessionId} status=${updatedSession.paymentStatus} requiredUSD=${requiredAmount} paidUSD=${paidAmount} deltaUSD=${deltaAmount} thresholdUSD=${MIN_OVERPAYMENT_THRESHOLD_USD} trigger=${shouldTrigger}`,
                  );

                  if (shouldTrigger) {
                    didTriggerOverpaymentRef.current = true;
                    onOverpaymentRefund(updatedSession);
                  }
                }

                window.parent?.postMessage(
                  { type: "sessionUpdate", session: updatedSession },
                  "*",
                );
              },
            }),
          );
        }

        await Promise.race(waitPromises);
      } catch (e) {
        logger.error(`[MultichainTransfer] ${e}`);
      }
    };

    startPolling();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [
    chainInfo.session,
    isBitcoinSession,
    selectedChainType,
    bitcoinLightningSession,
    glideConfig,
    onSuccess,
    step,
    isDocumentVisible,
    onInsufficientPaymentAmount,
    onSessionCreated,
    onOverpaymentRefund,
    depositConfig.mode,
  ]);
  const incompletePaidStr = activeSession?.actualPaymentAmount || "0";
  const incompleteRequiredStr =
    (activeSession && requiredPaymentAmount(activeSession)) || "0";
  const incompleteRemainingStr = isIncompletePayment
    ? subtractDecimalStrings(incompleteRequiredStr, incompletePaidStr)
    : "0";
  const incompleteCurrencySymbol = isIncompletePayment
    ? activeSession?.actualPaymentCurrencySymbol ||
      activeSession?.paymentCurrencySymbol
    : "";

  const formattedIncompleteRemaining = useMemo(() => {
    return formatRemainingAmount(
      incompleteRemainingStr,
      Number(activeSession?.actualPaymentAmountUSD) / Number(incompletePaidStr),
    );
  }, [
    incompleteRemainingStr,
    activeSession?.actualPaymentAmountUSD,
    incompletePaidStr,
  ]);

  // Incomplete payment: continue polling for the remaining funds
  useEffect(() => {
    if (!isIncompletePayment || !activeSession) return;

    const abortController = new AbortController();
    let didCallSuccess = false;

    waitForSession(glideConfig, {
      sessionId: activeSession.sessionId,
      signal: abortController.signal,
      onUpdate: (updatedSession) => {
        if (updatedSession.paymentStatus === "paid") {
          didCallSuccess = true;
          onSuccess(updatedSession);
        }
        if (updatedSession.paymentStatus === "pending_refund") {
          abortController.abort();
          onInsufficientPaymentAmount?.(updatedSession);
        }
      },
    })
      .then((completedSession) => {
        if (!didCallSuccess) {
          onSuccess(completedSession);
        }
      })
      .catch((error) => {
        if (error instanceof InsufficientPaymentAmountError) {
          onInsufficientPaymentAmount?.(activeSession);
          return;
        }
        if (error?.name === "AbortError") return;
        logger.error(
          `[MultichainTransfer/IncompletePayment] Polling error: ${error}`,
        );
        setIncompletePollingError(true);
      });

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIncompletePayment]);

  return (
    <>
      {onBack && !isIncompletePayment ? (
        <ModalBackButton
          onClick={handleBack}
          disabled={step === "wait_for_session"}
        />
      ) : null}

      {/* Amount header */}
      <div className="mt-2">
        <div className="flex flex-col items-center justify-center">
          <div className="text-sm font-medium text-[--color-text-primary]">
            {depositConfig.mode === "deposit" ? "Deposit on" : "Transfer on"}{" "}
            {chainInfo.name}
          </div>

          {isIncompletePayment && activeSession ? (
            <button
              className="flex items-center gap-2 px-2 py-1 cursor-pointer"
              onClick={async () => {
                await navigator.clipboard.writeText(incompleteRemainingStr);
                setDidCopyRemaining(true);
                setTimeout(() => setDidCopyRemaining(false), 2000);
              }}
              aria-label="Copy remaining amount"
            >
              {(activeSession.actualPaymentCurrencyLogoUrl ||
                activeSession.paymentCurrencyLogoUrl) &&
                (activeSession.actualPaymentChainLogoUrl ||
                  activeSession.paymentChainLogoUrl) && (
                  <LogoWithBadge
                    logoUrl={
                      activeSession.actualPaymentCurrencyLogoUrl ||
                      activeSession.paymentCurrencyLogoUrl
                    }
                    badgeUrl={
                      activeSession.actualPaymentChainLogoUrl ||
                      activeSession.paymentChainLogoUrl
                    }
                  />
                )}
              <span className="font-bold text-lg text-[--color-text-primary]">
                {formattedIncompleteRemaining} {incompleteCurrencySymbol}
              </span>
              {didCopyRemaining ? (
                <CheckCheck className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4 text-[--color-text-secondary]" />
              )}
            </button>
          ) : (
            depositConfig.hasFixedAmount &&
            chainInfo.session &&
            chainInfo.session.paymentAmount && (
              <motion.button
                whileTap={{ scale: 0.95 }}
                transition={animations.spring.medium}
                className="flex items-center gap-2 px-2 py-1 cursor-pointer group"
                onClick={async () => {
                  await navigator.clipboard.writeText(formattedPaymentAmount);
                  setDidCopyAmount(true);
                  setTimeout(() => setDidCopyAmount(false), 2000);
                }}
                aria-label="Copy amount"
              >
                {chainInfo.session.paymentCurrencyLogoUrl && (
                  <>
                    {isSingleChainCurrency && chainInfo.logoUrls?.[0] ? (
                      <LogoWithBadge
                        logoUrl={chainInfo.session.paymentCurrencyLogoUrl}
                        badgeUrl={chainInfo.logoUrls[0]}
                      />
                    ) : (
                      <img
                        src={chainInfo.session.paymentCurrencyLogoUrl}
                        alt={chainInfo.session.paymentCurrencySymbol}
                        className="w-6 h-6 rounded-full"
                      />
                    )}
                  </>
                )}
                <span className="font-bold text-lg text-[--color-text-primary]">
                  {formattedPaymentAmount}{" "}
                  {chainInfo.session.paymentCurrencySymbol}
                </span>
                <AnimatePresence mode="wait">
                  {didCopyAmount ? (
                    <motion.div
                      key="check"
                      initial={animations.scaleRotate.initial}
                      animate={animations.scaleRotate.animate}
                      exit={animations.scaleRotate.exit}
                      transition={animations.spring.bouncy}
                    >
                      <CheckCheck className="w-4 h-4 text-green-600" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="copy"
                      initial={{ scale: 1 }}
                      exit={{ scale: 0, rotate: 180 }}
                      transition={animations.spring.stiff}
                    >
                      <Copy className="w-4 h-4 text-[--color-text-secondary]" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            )
          )}
        </div>
      </div>

      <div className="mt-4 mb-3 w-full h-px bg-[--color-modal-border]" />

      {/* Token and Chain Selector — locked when incomplete payment detected */}
      {(isIncompletePayment ||
        ((tokenOptions.length >= 1 || chainOptions.length >= 1) &&
          selectedCurrency &&
          allAvailableCurrencies)) && (
        <div className="text-xs font-medium text-[--color-text-secondary] mb-1.5">
          Select token and chain
        </div>
      )}
      {isIncompletePayment && activeSession ? (
        <div className="mb-3">
          {/* Show locked selector for incomplete payment */}
          <CombinedTokenChainSelector
            currencies={
              selectedCurrency
                ? [selectedCurrency]
                : (currencyVariants as Currency[])
            }
            chains={allowedChains}
            selectedCurrencyId={selectedCurrency?.id || ""}
            selectedChainId={chainInfo.session?.paymentChainId || ""}
            onSelect={handleCombinedSelection}
            disabled={true}
            className="w-full"
          />
        </div>
      ) : (
        (tokenOptions.length >= 1 || chainOptions.length >= 1) &&
        selectedCurrency &&
        allAvailableCurrencies && (
          <div className="mb-3">
            {/* Show combined selector for normal operation */}
            <CombinedTokenChainSelector
              currencies={filteredCurrencies}
              chains={allowedChains}
              selectedCurrencyId={selectedCurrency.id}
              selectedChainId={
                chainInfo.session?.paymentChainId || chainOptions[0]?.id || ""
              }
              onSelect={handleCombinedSelection}
              disabled={isCreatingChainSession}
              className="w-full"
            />
          </div>
        )
      )}
      {/* QR + Address row */}
      <div className="mt-5 flex items-start gap-3">
        <div className="flex-shrink-0">
          <motion.div
            key={qrCodeValue}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...animations.spring.soft, duration: 0.4 }}
            className="w-[118px] h-[118px] rounded-lg shadow-sm bg-white flex items-center justify-center relative overflow-hidden"
          >
            <div className="relative w-full h-full p-0.5">
              <QRCode
                value={qrCodeValue}
                size={
                  selectedChainType === "bitcoin" && useLightningOnly ? 75 : 100
                }
                dotsType="dots"
                errorCorrectionLevel={
                  selectedChainType === "bitcoin" ? "L" : "Q"
                }
              />
            </div>
            {isSingleChainCurrency && chainInfo.logoUrls?.[0] && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-md border-2 border-white p-0.5">
                  <img
                    src={chainInfo.logoUrls[0]}
                    alt={chainInfo.name}
                    className="w-full h-full rounded-full"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </div>
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div
            className={`bg-[--color-background-secondary] rounded-lg p-2 flex flex-col shadow-sm ${
              selectedChainType === "bitcoin" && shouldWaitForLightning
                ? "h-[82px]"
                : "h-[118px]"
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <h4 className="text-[--color-text-secondary] text-xs">
                {selectedChainType === "bitcoin" &&
                useLightningOnly &&
                shouldWaitForLightning
                  ? "Lightning Invoice"
                  : "Transfer Address"}
              </h4>
              {/* Overflow menu: edge-case action, kept away from the copy target */}
              {onRegenerateAddress && (
                <div className="relative flex-shrink-0">
                  <button
                    className="p-0.5 rounded-md hover:bg-[--color-background-tertiary] transition-colors duration-200 disabled:opacity-50"
                    onClick={() => setIsAddressMenuOpen((open) => !open)}
                    disabled={isRegeneratingAddress}
                    aria-label="Address options"
                    aria-haspopup="menu"
                    aria-expanded={isAddressMenuOpen}
                  >
                    <MoreVertical className="w-4 h-4 text-[--color-text-secondary]" />
                  </button>
                  {isAddressMenuOpen && (
                    <>
                      {/* Transparent backdrop closes the menu on any outside click */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsAddressMenuOpen(false)}
                      />
                      <div
                        role="menu"
                        className="absolute right-full mr-1 top-1/2 -translate-y-1/2 bg-[--color-modal-background] border border-[--color-background-secondary] rounded-lg shadow-lg z-50 overflow-hidden"
                      >
                        <button
                          role="menuitem"
                          onClick={handleRegenerateAddress}
                          className="py-2 px-4 text-xs text-[--color-text-primary] hover:bg-[--color-background-secondary] transition-colors whitespace-nowrap w-full text-left"
                        >
                          {isRegeneratingAddress
                            ? "Generating…"
                            : "Generate new address"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-auto">
              <div className="flex items-start justify-between gap-2 h-full">
                <div className="flex-1 pr-2 overflow-auto">
                  <motion.button
                    type="button"
                    whileTap={{ scale: 0.99 }}
                    transition={animations.spring.medium}
                    onClick={copyAddress}
                    disabled={chainInfo.isPlaceholder}
                    aria-label="Copy deposit address"
                    className="text-left group w-full"
                  >
                    <span className="font-mono text-xs text-[--color-text-primary] break-all leading-relaxed">
                      {chainInfo.address}
                      <span className="inline-flex items-center align-middle ml-1 p-0.5 rounded-md relative -top-0.5 whitespace-nowrap">
                        <AnimatePresence mode="wait">
                          {didCopyAddress ? (
                            <motion.span
                              key="check"
                              className="inline-block"
                              initial={animations.scaleRotate.initial}
                              animate={animations.scaleRotate.animate}
                              exit={animations.scaleRotate.exit}
                              transition={animations.spring.bouncy}
                            >
                              <CheckCheck className="w-4 h-4 text-green-600" />
                            </motion.span>
                          ) : (
                            <motion.span
                              key="copy"
                              className="inline-block"
                              initial={{ scale: 1 }}
                              exit={{ scale: 0, rotate: 180 }}
                              transition={animations.spring.stiff}
                            >
                              <Copy className="w-4 h-4 text-[--color-text-secondary] group-hover:text-[--color-text-primary] transition-colors" />
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </span>
                    </span>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
          {/* Lightning toggle for Bitcoin - separate box below */}
          {selectedChainType === "bitcoin" && shouldWaitForLightning && (
            <div className="bg-[--color-background-secondary] rounded-lg px-3 py-1.5 shadow-sm flex items-center justify-between">
              <span className="text-xs text-[--color-text-secondary] font-medium">
                Lightning
              </span>
              <button
                onClick={() => setUseLightningOnly(!useLightningOnly)}
                className="flex items-center"
              >
                <div
                  className={`w-8 h-4 rounded-full transition-colors ${useLightningOnly ? "bg-blue-500" : "bg-gray-300"} relative`}
                >
                  <div
                    className={`w-3 h-3 bg-white rounded-full absolute top-0.5 transition-transform ${useLightningOnly ? "translate-x-4" : "translate-x-0.5"}`}
                  />
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
      {isIncompletePayment && activeSession ? (
        <WarningAlert
          error={
            incompletePollingError ? "Please refresh and try again." : undefined
          }
          action={{
            label: "Cancel and request a refund",
            icon: <Undo2 className="w-3 h-3 flex-shrink-0" />,
            onAction: () => onRequestRefund?.(activeSession),
          }}
        >
          <div className="font-semibold mb-1">Partial Payment Detected</div>
          <div>
            We detected a payment of{" "}
            <span className="font-semibold">
              {formatDisplayAmount(incompletePaidStr)}{" "}
              {incompleteCurrencySymbol}
            </span>
            . Please send the remaining amount to the same address to complete
            your payment.
          </div>
        </WarningAlert>
      ) : (
        <div className="space-y-2 mt-5">
          {!didUserConfirmDeposit && (
            <PrimaryButton
              className="mt-4"
              onClick={() => {
                setDidUserConfirmDeposit(true);
                if (isPollingAborted) handleResumePolling();
              }}
              disabled={chainInfo.isPlaceholder || !chainInfo.session}
            >
              Deposit complete
            </PrimaryButton>
          )}
          {didUserConfirmDeposit &&
          step === "waiting_for_transfer" &&
          !isPollingAborted &&
          isDocumentVisible &&
          paymentStatus === "unpaid" ? (
            <div className="w-full">
              <Alert
                icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                title={`Waiting for ${
                  depositConfig.mode === "deposit" ? "deposit" : "transfer"
                }${showUpdateIndicator ? "..." : ".."}`}
                subtitle={`We're waiting for your ${
                  depositConfig.mode === "deposit" ? "deposit" : "transfer"
                }. This can take a minute after you have initiated it.`}
              />
            </div>
          ) : didUserConfirmDeposit &&
            step === "waiting_for_transfer" &&
            !isPollingAborted &&
            isDocumentVisible &&
            paymentStatus === "waiting_confirmations" ? (
            <div className="w-full">
              <Alert
                icon={<CircleCheck strokeWidth={2.25} className="w-4 h-4" />}
                title={`${
                  depositConfig.mode === "deposit" ? "Deposit" : "Transfer"
                } received`}
                subtitle={`Confirming your ${
                  depositConfig.mode === "deposit" ? "deposit" : "transfer"
                }. ${
                  (chainInfo.session?.etaInSeconds ?? 0) > 10
                    ? `This can take up to ${getETA(chainInfo.session?.etaInSeconds)}.`
                    : "This takes a few seconds."
                }`}
              />
            </div>
          ) : didUserConfirmDeposit &&
            step === "waiting_for_transfer" &&
            !isDocumentVisible ? (
            <div className="w-full">
              <Alert
                icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                title="Monitoring paused"
                subtitle="Switch back to this tab to resume checking for your transfer."
              />
            </div>
          ) : didUserConfirmDeposit &&
            step === "waiting_for_transfer" &&
            isPollingAborted ? (
            <div className="w-full space-y-3">
              <Alert
                icon={<Clock strokeWidth={2.25} className="w-4 h-4" />}
                title={`Ready to check for your ${
                  depositConfig.mode === "deposit" ? "deposit" : "transfer"
                }?`}
                subtitle={`Let us know when you've completed your ${
                  depositConfig.mode === "deposit" ? "deposit" : "transfer"
                } and we'll check for it.`}
              />
              <div className="flex justify-center">
                <SecondaryButton
                  title={
                    depositConfig.mode === "deposit"
                      ? "Check deposit"
                      : "Check transfer"
                  }
                  onClick={handleResumePolling}
                  center
                />
              </div>
            </div>
          ) : null}

          {/* Bitcoin-specific warnings */}
          {selectedChainType === "bitcoin" &&
            !useLightningOnly &&
            (depositConfig.mode === "pay" ? (
              <Warning>
                <div>
                  Send the required amount, accounting for any withdrawal fees
                  your exchange might deduct.
                </div>
                <div>
                  Refunds for failed payments or overpayments are sent in USDC
                  on Ethereum.
                </div>
              </Warning>
            ) : (
              <Warning>
                <div>
                  Minimum deposit: <span className="font-bold">0.0001 BTC</span>
                </div>
                <div>
                  Sending less than this amount may result in delays or loss of
                  funds.
                </div>
              </Warning>
            ))}
        </div>
      )}
    </>
  );
}
